/**
 * The PAM player card (ADR-047, task B3).
 *
 * One selected player: Profile · Protection · History · Balances · Statement ·
 * Notes · Tickets · Risk · Bonuses · VIP. The card is one server call
 * (`GET /internal/admin/players/:id`); the statement, the protective state and
 * the history are loaded lazily when their tab is opened, because those are the
 * lists that can be long.
 *
 * Protection (ADR-070) and History (ADR-068) live in `PlayerProtection.tsx` —
 * both are read-only, and the first is a pure derivation over the second. Why
 * Protection has no controls is argued in that file's header: the server has no
 * code path that shortens an exclusion or raises a limit early, so a control
 * here could only ever fail.
 *
 * Nothing here writes a balance — there is no adjustment control, by the same
 * argument the old Players screen made: a balance edit box is how an operator
 * panel becomes the second writer of money. Status, profile and notes are the
 * only writes, and each is confirmed and audited on the server.
 */
import { useState, type ReactNode } from 'react';
import { fetchVipTiers } from '../api/client.ts';
import type { PlayerSummary, UserStatus } from '../api/types.ts';
import {
  addNote,
  fetchNotes,
  fetchPlayerCard,
  fetchPlayerVip,
  fetchStatement,
  setPlayerStatus,
  setVipOverride,
  updatePlayerProfile,
  type BonusGrantView,
  type CurrencyBalance,
  type PlayerCard as PlayerCardData,
  type PlayerNoteView,
  type RiskFlagView,
  type TicketSummary,
} from '../api/support.ts';
import { EventsTab, ProtectionTab } from './PlayerProtection.tsx';
import { useAsync } from '../lib/useAsync.ts';
import { absolute, age, shortTime } from '../lib/datetime.ts';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Money,
  StatusBadge,
  TableSkeleton,
} from '../ui/primitives.tsx';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { IconCheck, IconDiamond, IconInbound, IconPeople } from '../ui/icons.tsx';

type TabId =
  | 'profile'
  | 'protection'
  | 'events'
  | 'balances'
  | 'statement'
  | 'notes'
  | 'tickets'
  | 'risk'
  | 'bonuses'
  | 'vip';

const TABS: { id: TabId; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  // ADR-070 and ADR-068, next to the profile because both answer "who is this
  // person and what has happened to their account" — the questions a dispute
  // and a compliance check both start from.
  { id: 'protection', label: 'Protection' },
  { id: 'events', label: 'History' },
  { id: 'balances', label: 'Balances' },
  { id: 'statement', label: 'Statement' },
  { id: 'notes', label: 'Notes' },
  { id: 'tickets', label: 'Tickets' },
  { id: 'risk', label: 'Risk' },
  { id: 'bonuses', label: 'Bonuses' },
  { id: 'vip', label: 'VIP' },
];

function ticketTone(status: string): 'info' | 'pending' | 'win' | 'neutral' {
  return status === 'open' ? 'info' : status === 'pending' ? 'pending' : status === 'resolved' ? 'win' : 'neutral';
}

export function PlayerCard({
  player,
  onChanged,
}: {
  player: PlayerSummary | null;
  onChanged: () => void;
}): ReactNode {
  const [tab, setTab] = useState<TabId>('profile');
  const userId = player?.userId ?? 0;

  const card = useAsync(
    () => (player ? fetchPlayerCard(userId, player) : Promise.resolve(null)),
    [userId],
  );

  if (!player) {
    return (
      <Card title="Player" icon={<IconPeople />}>
        <EmptyState
          compact
          icon={<IconPeople size="lg" />}
          title="No player selected"
          hint="Pick a row to open the card — profile, per-currency balances, the ledger, notes, tickets and the risk seams."
        />
      </Card>
    );
  }

  const data = card.data;
  const count = (id: TabId): number | undefined => {
    if (!data) return undefined;
    if (id === 'balances') return data.balances.length;
    if (id === 'notes') return data.notes.length;
    if (id === 'tickets') return data.tickets.length;
    if (id === 'risk') return data.riskFlags.filter((f) => f.status === 'open').length || undefined;
    if (id === 'bonuses') return data.bonusGrants.length || undefined;
    return undefined;
  };

  return (
    <div className="stack">
      <Card title={player.username} sub={`#${player.userId}`} icon={<IconPeople />}
        actions={<StatusBadge status={player.status} />}>
        <div className="tabs">
          {TABS.map((t) => {
            const c = count(t.id);
            return (
              <button
                key={t.id}
                type="button"
                className={`tab${tab === t.id ? ' is-active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {c === undefined ? null : <span className="tab__count">{c}</span>}
              </button>
            );
          })}
        </div>

        {card.error ? (
          <ErrorState title="The player card did not load" detail={card.error} onRetry={card.reload} />
        ) : !data ? (
          <TableSkeleton columns={['text', 'text', 'num', 'num']} rows={5} />
        ) : (
          <>
            {tab === 'profile' && (
              <ProfileTab card={data} onChanged={() => { card.reload(); onChanged(); }} />
            )}
            {tab === 'protection' && <ProtectionTab userId={userId} profile={data.profile} />}
            {tab === 'events' && <EventsTab userId={userId} />}
            {tab === 'balances' && <BalancesTab balances={data.balances} />}
            {tab === 'statement' && <StatementTab userId={userId} />}
            {tab === 'notes' && <NotesTab userId={userId} notes={data.notes} onChanged={card.reload} />}
            {tab === 'tickets' && <TicketsTab tickets={data.tickets} />}
            {tab === 'risk' && <RiskTab flags={data.riskFlags} />}
            {tab === 'bonuses' && <BonusesTab grants={data.bonusGrants} />}
            {tab === 'vip' && <VipTab userId={userId} />}
          </>
        )}
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------- profile

function ProfileTab({ card, onChanged }: { card: PlayerCardData; onChanged: () => void }): ReactNode {
  const { player, profile } = card;
  const confirm = useConfirm();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function changeStatus(next: UserStatus): Promise<void> {
    const suspending = next === 'suspended';
    const result = await confirm({
      title: suspending ? 'Suspend this account' : 'Reinstate this account',
      intent: suspending ? 'danger' : 'default',
      body: suspending
        ? 'A suspended account cannot bet, deposit or withdraw. Any balance stays exactly where it is — suspension is not a confiscation.'
        : 'The account returns to active and can bet, deposit and withdraw again.',
      confirmLabel: suspending ? 'Suspend' : 'Reinstate',
      facts: [
        { label: 'Player', value: `${player.username} #${player.userId}` },
        { label: 'Current status', value: player.status },
      ],
      note: { label: 'Reason', required: true, hint: 'Recorded against the account.' },
    });
    if (!result.confirmed) return;
    setBusy(true);
    try {
      await setPlayerStatus(player.userId, next, result.note);
      toast(`${player.username} is now ${next}.`);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not change the status.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function editProfile(): Promise<void> {
    const country = window.prompt('Country (ISO alpha-2, e.g. IN) — blank to clear', profile.countryCode ?? '');
    if (country === null) return;
    const locale = window.prompt('Locale (BCP-47, e.g. en or en-IN) — blank to clear', profile.locale ?? '');
    if (locale === null) return;
    const marketing = window.confirm('Marketing opt-in? OK = yes, Cancel = no');
    setBusy(true);
    try {
      await updatePlayerProfile(player.userId, {
        countryCode: country.trim() === '' ? null : country.trim(),
        locale: locale.trim() === '' ? null : locale.trim(),
        marketingOptIn: marketing,
      });
      toast('Profile updated.');
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not update the profile.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="kv kv--spaced">
        <span className="kv__k">User id</span>
        <span className="kv__v num">{player.userId}</span>
        <span className="kv__k">Status</span>
        <span className="kv__v"><StatusBadge status={player.status} /></span>
        <span className="kv__k">Email</span>
        <span className="kv__v">{player.email ?? '—'}</span>
        <span className="kv__k">Phone</span>
        <span className="kv__v">{player.phone ?? '—'}</span>
        <span className="kv__k">Country</span>
        <span className="kv__v">{profile.countryCode ?? '—'}</span>
        <span className="kv__k">Locale</span>
        <span className="kv__v">{profile.locale ?? '—'}</span>
        <span className="kv__k">Marketing opt-in</span>
        <span className="kv__v">{profile.marketingOptIn === null ? '— (never asked)' : profile.marketingOptIn ? 'Yes' : 'No'}</span>
        <span className="kv__k">Acquisition</span>
        <span className="kv__v">{profile.acquisitionSource ?? '—'}</span>
        <span className="kv__k">Retention score</span>
        <span className="kv__v">{profile.retentionScore ?? '— (AI seam, unwritten)'}</span>
        <span className="kv__k">Last login</span>
        <span className="kv__v" title={absolute(profile.lastLoginAt)}>{age(profile.lastLoginAt)}</span>
        <span className="kv__k">Joined</span>
        <span className="kv__v">{shortTime(player.createdAt)}</span>
        <span className="kv__k">Sessions</span>
        <span className="kv__v">{card.sessions.filter((s) => s.active).length} active · {card.sessions.length} recent</span>
      </div>

      <div className="row-actions row-actions--start">
        <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={() => void editProfile()}>
          Edit profile
        </button>
        {player.status === 'active' ? (
          <button type="button" className="btn btn--danger btn--sm" disabled={busy} onClick={() => void changeStatus('suspended')}>
            Suspend account
          </button>
        ) : player.status === 'suspended' ? (
          <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => void changeStatus('active')}>
            Reinstate account
          </button>
        ) : (
          <span className="field__hint">This account is closed.</span>
        )}
      </div>
    </>
  );
}

// ------------------------------------------------------------------ balances

function BalancesTab({ balances }: { balances: CurrencyBalance[] }): ReactNode {
  if (balances.length === 0) {
    return <EmptyState compact title="No accounts" hint="This player holds no currency accounts." />;
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Currency</th>
            <th className="right">Balance</th>
            <th className="right">Deposited</th>
            <th className="right">Withdrawn</th>
            <th className="right">Wagered</th>
            <th className="right">Won</th>
          </tr>
        </thead>
        <tbody>
          {balances.map((b) => (
            <tr key={b.currency}>
              <td className="cell-lead nowrap">{b.currency}</td>
              <td className="right mono"><Money value={b.balance} places={4} /></td>
              <td className="right mono dim"><Money value={b.deposited} places={4} /></td>
              <td className="right mono dim"><Money value={b.withdrawn} places={4} /></td>
              <td className="right mono dim"><Money value={b.wagered} places={4} /></td>
              <td className="right mono dim"><Money value={b.won} places={4} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ----------------------------------------------------------------- statement

function StatementTab({ userId }: { userId: number }): ReactNode {
  const statement = useAsync(() => fetchStatement(userId, 50), [userId]);
  const entries = statement.data ?? [];
  if (statement.loading && entries.length === 0) {
    return <TableSkeleton columns={['text', 'text', 'text', 'num', 'num']} rows={6} />;
  }
  if (entries.length === 0) {
    return <EmptyState compact title="No ledger entries" hint="Nothing has moved on this account." />;
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Id</th><th>When</th><th>Kind</th><th>Ccy</th><th>Ref</th>
            <th className="right">Amount</th><th className="right">Balance after</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <td className="mono dim">{e.id}</td>
              <td className="nowrap muted" title={absolute(e.createdAt)}>{shortTime(e.createdAt)}</td>
              <td>{e.kind}</td>
              <td className="mono dim">{e.currency}</td>
              <td className="muted nowrap">{e.refType} #{e.refId}</td>
              <td className="right mono"><Money value={e.amount} colour signed places={4} /></td>
              <td className="right mono dim"><Money value={e.balanceAfter} places={4} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --------------------------------------------------------------------- notes

function NotesTab({
  userId,
  notes,
  onChanged,
}: {
  userId: number;
  notes: PlayerNoteView[];
  onChanged: () => void;
}): ReactNode {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const live = useAsync(() => fetchNotes(userId), [userId]);
  const rows = live.data ?? notes;

  async function submit(): Promise<void> {
    if (draft.trim() === '' || busy) return;
    setBusy(true);
    try {
      await addNote(userId, draft.trim());
      setDraft('');
      toast('Note added.');
      live.reload();
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not add the note.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack stack--tight">
      <form
        className="field"
        onSubmit={(e) => { e.preventDefault(); void submit(); }}
      >
        <span className="field__label">Add a note</span>
        <textarea
          className="input"
          rows={2}
          value={draft}
          placeholder="Append-only. A wrong note is corrected by a newer note, never edited."
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="row-actions row-actions--start">
          <button type="submit" className="btn btn--sm btn--primary" disabled={busy || draft.trim() === ''}>
            Add note
          </button>
        </div>
      </form>

      {rows.length === 0 ? (
        <EmptyState compact title="No notes yet" hint="Notes are evidence — append-only by construction." />
      ) : (
        <div className="thread">
          {rows.map((n) => (
            <div key={n.id} className={`note${n.pinned ? ' note--pinned' : ''}`}>
              <div className="note__head">
                <span>{n.operatorUsername ?? `operator #${n.operatorId}`}</span>
                <span title={absolute(n.createdAt)}>{shortTime(n.createdAt)}</span>
                {n.pinned ? <Badge tone="accent">pinned</Badge> : null}
              </div>
              <div className="msg__body">{n.note}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------- tickets

function TicketsTab({ tickets }: { tickets: TicketSummary[] }): ReactNode {
  if (tickets.length === 0) {
    return <EmptyState compact icon={<IconInbound size="lg" />} title="No tickets" hint="This player has opened no support threads." />;
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr><th>#</th><th>Subject</th><th>Status</th><th>Assignee</th><th className="right">Msgs</th><th className="right">Updated</th></tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <tr key={t.id}>
              <td className="mono dim">{t.id}</td>
              <td className="cell-lead">
                {t.subject}
                {t.triage?.disposition === 'draft' ? (
                  <Badge tone="info">AI draft — not sent</Badge>
                ) : t.triage ? (
                  <Badge tone="info">triaged</Badge>
                ) : null}
              </td>
              <td><Badge tone={ticketTone(t.status)}>{t.status}</Badge></td>
              <td className="muted nowrap">{t.assignedOperatorUsername ?? '—'}</td>
              <td className="right mono dim">{t.messageCount}</td>
              <td className="right mono muted" title={absolute(t.updatedAt)}>{age(t.updatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------- risk

function RiskTab({ flags }: { flags: RiskFlagView[] }): ReactNode {
  if (flags.length === 0) {
    return (
      <EmptyState
        compact
        icon={<IconDiamond size="lg" />}
        title="No risk flags"
        hint="The AI risk monitor writes here (ADR-056). Empty until it raises a verdict — the queue exists before its writer."
      />
    );
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr><th>#</th><th>Kind</th><th>Status</th><th>Detail</th><th className="right">Raised</th></tr></thead>
        <tbody>
          {flags.map((f) => (
            <tr key={f.id}>
              <td className="mono dim">{f.id}</td>
              <td className="cell-lead nowrap">{f.kind}</td>
              <td><Badge tone={f.status === 'open' ? 'loss' : 'neutral'}>{f.status}</Badge></td>
              <td className="muted mono">{f.detail ? JSON.stringify(f.detail).slice(0, 80) : '—'}</td>
              <td className="right mono muted" title={absolute(f.createdAt)}>{age(f.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------------- bonuses

function BonusesTab({ grants }: { grants: BonusGrantView[] }): ReactNode {
  if (grants.length === 0) {
    return (
      <EmptyState
        compact
        icon={<IconCheck size="lg" />}
        title="No bonus grants"
        hint="The P2 bonus engine writes here. Empty until a campaign grants this player a bonus."
      />
    );
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr><th>#</th><th>Ccy</th><th className="right">Amount</th><th className="right">Locked</th><th className="right">Progress</th><th>Status</th></tr>
        </thead>
        <tbody>
          {grants.map((g) => (
            <tr key={g.id}>
              <td className="mono dim">{g.id}</td>
              <td className="nowrap">{g.currency}</td>
              <td className="right mono"><Money value={g.amount} places={4} /></td>
              <td className="right mono dim"><Money value={g.lockedRemaining} places={4} /></td>
              <td className="right mono dim"><Money value={g.wagerProgress} places={4} /> / <Money value={g.wagerRequired} places={4} /></td>
              <td><Badge tone="neutral">{g.status}</Badge></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ----------------------------------------------------------- VIP (ADR-066)

/**
 * The player's VIP standing, and the audited tier pin. No money moves here —
 * the pin changes which rakeback rate future accrual uses; XP and the derived
 * tier keep tracking play underneath, so clearing the pin lands the player
 * exactly where the ladder says.
 */
function VipTab({ userId }: { userId: number }): ReactNode {
  const vip = useAsync(() => fetchPlayerVip(userId), [userId]);
  const ladder = useAsync(() => fetchVipTiers(), []);
  const [pick, setPick] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  if (vip.error) {
    return <ErrorState title="VIP standing did not load" detail={vip.error} onRetry={vip.reload} />;
  }
  if (vip.loading || !vip.data) return <TableSkeleton columns={['text', 'num']} rows={3} />;

  const v = vip.data;
  const tiers = ladder.data ?? [];
  const nameOf = (tier: number): string => tiers.find((t) => t.tier === tier)?.name ?? `tier ${tier}`;

  async function apply(tier: number | null): Promise<void> {
    if (busy || reason.trim() === '') return;
    setBusy(true);
    try {
      await setVipOverride(userId, tier, reason.trim());
      toast(tier === null ? 'Override cleared — the ladder rules again.' : `Pinned to ${nameOf(tier)}.`);
      setReason('');
      setPick('');
      vip.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not change the override.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <table className="table">
        <tbody>
          <tr>
            <td>Effective tier</td>
            <td className="num">
              <Badge tone={v.tierOverride !== null ? 'pending' : 'neutral'}>
                {v.effectiveName} · rank {v.effectiveTier}
              </Badge>{' '}
              {v.tierOverride !== null ? <Badge tone="pending">pinned by operator</Badge> : null}
            </td>
          </tr>
          <tr>
            <td>Ladder says (from XP)</td>
            <td className="num">{nameOf(v.derivedTier)} · rank {v.derivedTier}</td>
          </tr>
          <tr>
            <td>XP</td>
            <td className="num">{v.xp}</td>
          </tr>
          <tr>
            <td>Rakeback rate</td>
            <td className="num">{(v.rakebackBps / 100).toFixed(2)}%</td>
          </tr>
          {v.unclaimedRakeback.map((u) => (
            <tr key={u.currency}>
              <td>Unclaimed rakeback ({u.currency})</td>
              <td className="num"><Money value={u.amount} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="subcard">
        <p className="field__hint">
          Pin the tier (benefits included, audited). The reason is recorded in the audit log.
        </p>
        <div className="row-actions row-actions--start">
          <select
            className="input input--sm"
            value={pick === '' ? '' : String(pick)}
            onChange={(e) => setPick(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Pick a tier…</option>
            {tiers.map((t) => (
              <option key={t.tier} value={t.tier}>
                {t.name} · rank {t.tier} · {(t.rakebackBps / 100).toFixed(2)}% rakeback
              </option>
            ))}
          </select>
          <input
            className="input input--sm"
            placeholder="Reason (required, audited)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button
            type="button"
            className="btn btn--sm btn--primary"
            disabled={busy || pick === '' || reason.trim() === ''}
            onClick={() => { if (pick !== '') void apply(pick); }}
          >
            Pin tier
          </button>
          {v.tierOverride !== null ? (
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              disabled={busy || reason.trim() === ''}
              onClick={() => void apply(null)}
            >
              Clear pin
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
